import { useTranslation } from 'react-i18next';

import { Combobox, type ComboboxOption } from '@/shared/ui';

import { roleChoiceValue, STANDARD_PREFIX, CUSTOM_PREFIX, type WorkspaceRoleChoice } from './roles';
import { ASSIGNABLE_ROLES, type CustomRole, type MemberRole } from './types';

interface Props {
  value: MemberRole;
  onChange: (role: MemberRole) => void;
  label: string;
  roles?: readonly MemberRole[];
  disabled?: boolean;
  /** `md` (2.5rem) in forms next to text fields, `sm` in dense rows. */
  size?: 'sm' | 'md';
}

/** A role picker (searchable, keyboard friendly); role names are translated, ids are stable. */
export function RoleSelect({
  value,
  onChange,
  label,
  roles = ASSIGNABLE_ROLES,
  disabled,
  size = 'sm',
}: Props) {
  const { t } = useTranslation();
  const list = roles.includes(value) ? roles : [value, ...roles];
  const options: ComboboxOption[] = list.map((role) => ({
    value: role,
    label: t(`roles.${role}`),
  }));
  return (
    <Combobox
      label={label}
      options={options}
      value={value}
      searchPlaceholder={t('common.search')}
      emptyText={t('common.noResults')}
      size={size}
      {...(disabled !== undefined && { disabled })}
      onChange={(next) => {
        onChange(next as MemberRole);
      }}
    />
  );
}

interface WorkspaceRoleProps {
  value: WorkspaceRoleChoice;
  onChange: (choice: WorkspaceRoleChoice) => void;
  label: string;
  customRoles: readonly CustomRole[];
  /** Standard roles the editor may hand out (owner is never listed). */
  standardRoles?: readonly MemberRole[];
  /** Custom roles the editor may not hand out are shown but disabled. */
  isGrantable?: (role: CustomRole) => boolean;
  disabled?: boolean;
}

/**
 * Single searchable selector for the workspace role: standard and custom roles in one list
 * (grouped), so there is never a standard role "plus" a custom one.
 */
export function WorkspaceRoleSelect({
  value,
  onChange,
  label,
  customRoles,
  standardRoles = ASSIGNABLE_ROLES,
  isGrantable,
  disabled,
}: WorkspaceRoleProps) {
  const { t } = useTranslation();
  const currentStandard = value.role !== null && !standardRoles.includes(value.role);
  const standard = currentStandard && value.role ? [value.role, ...standardRoles] : standardRoles;
  const options: ComboboxOption[] = [
    ...standard.map((role) => ({
      value: `${STANDARD_PREFIX}${role}`,
      label: t(`roles.${role}`),
      group: t('roles.groupStandard'),
      disabled: role === 'owner',
    })),
    ...customRoles.map((role) => ({
      value: `${CUSTOM_PREFIX}${role.id}`,
      label: role.name,
      ...(role.description ? { description: role.description } : {}),
      group: t('roles.groupCustom'),
      disabled: isGrantable ? !isGrantable(role) : false,
    })),
  ];
  return (
    <Combobox
      label={label}
      options={options}
      value={roleChoiceValue(value)}
      searchPlaceholder={t('common.search')}
      emptyText={t('common.noResults')}
      size="sm"
      {...(disabled !== undefined && { disabled })}
      onChange={(next) => {
        if (next.startsWith(CUSTOM_PREFIX)) {
          onChange({ role: null, customRoleId: next.slice(CUSTOM_PREFIX.length) });
        } else {
          onChange({ role: next.slice(STANDARD_PREFIX.length) as MemberRole, customRoleId: null });
        }
      }}
    />
  );
}
